import { Directive, ElementRef, HostListener, OnDestroy, OnInit, Optional, Renderer2, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Directive({
  selector: '[appPhoneMask]',
  standalone: true,
})
export class PhoneMaskDirective implements OnInit, OnDestroy {
  private isApplyingValue = false;
  private controlChanges?: Subscription;

  constructor(
    private readonly elementRef: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2,
    @Optional() @Self() private readonly ngControl: NgControl | null,
  ) {}

  ngOnInit(): void {
    this.formatInitialValue();

    if (this.ngControl?.control) {
      this.controlChanges = this.ngControl.control.valueChanges.subscribe((value) => {
        if (this.isApplyingValue) {
          return;
        }

        const digits = this.getOnlyDigits((value ?? '').toString()).slice(0, 10);
        this.applyValue(digits, digits.length, false, undefined, true);
      });
    }
  }

  ngOnDestroy(): void {
    this.controlChanges?.unsubscribe();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    event.preventDefault();

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const allowedKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'];
    if (allowedKeys.includes(event.key)) {
      return;
    }

    const input = this.elementRef.nativeElement;
    const rawValue = input.value;
    const digits = this.getOnlyDigits(rawValue);
    const selectionStart = input.selectionStart ?? rawValue.length;
    const selectionEnd = input.selectionEnd ?? rawValue.length;
    const rawSelectionStart = this.countDigitsBefore(rawValue, selectionStart);
    const rawSelectionEnd = this.countDigitsBefore(rawValue, selectionEnd);
    const hasSelection = rawSelectionStart !== rawSelectionEnd;

    if (event.key === 'Backspace') {
      const newDigits = this.removeDigits(digits, rawSelectionStart, rawSelectionEnd, 'Backspace');
      const cursorPosition = hasSelection ? rawSelectionStart : rawSelectionStart - 1;
      this.applyValue(newDigits, cursorPosition, undefined, input);
      return;
    }

    if (event.key === 'Delete') {
      const newDigits = this.removeDigits(digits, rawSelectionStart, rawSelectionEnd, 'Delete');
      this.applyValue(newDigits, rawSelectionStart, undefined, input);
      return;
    }

    const isDigit = /^[0-9]$/.test(event.key);
    if (!isDigit) {
      return;
    }

    if (digits.length >= 10 && !hasSelection) {
      return;
    }

    const newDigits = this.insertDigit(digits, rawSelectionStart, rawSelectionEnd, event.key);
    const cursorPosition = rawSelectionStart + 1;
    this.applyValue(newDigits, cursorPosition, undefined, input);
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    const selectionStart = input.selectionStart ?? rawValue.length;
    const digitsBeforeCursor = this.countDigitsBefore(rawValue, selectionStart);
    const digits = this.getOnlyDigits(rawValue).slice(0, 10);

    this.applyValue(digits, digitsBeforeCursor, undefined, input);
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const input = event.target as HTMLInputElement;
    const pastedText = event.clipboardData?.getData('text/plain') ?? '';
    const digits = this.getOnlyDigits(pastedText).slice(0, 10);
    this.applyValue(digits, digits.length, undefined, input);
  }

  private getOnlyDigits(value: string): string {
    return value.replace(/\D/g, '');
  }

  private insertDigit(digits: string, start: number, end: number, digit: string): string {
    return `${digits.slice(0, start)}${digit}${digits.slice(end)}`.slice(0, 10);
  }

  private removeDigits(digits: string, start: number, end: number, type: 'Backspace' | 'Delete'): string {
    if (start !== end) {
      return `${digits.slice(0, start)}${digits.slice(end)}`;
    }

    if (type === 'Backspace' && start > 0) {
      return `${digits.slice(0, start - 1)}${digits.slice(start)}`;
    }

    if (type === 'Delete' && start < digits.length) {
      return `${digits.slice(0, start)}${digits.slice(start + 1)}`;
    }

    return digits;
  }

  private formatValue(digits: string): string {
    if (!digits) {
      return '';
    }

    if (digits.length <= 3) {
      return `(${digits}${digits.length === 3 ? ')' : ''}`;
    }

    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  private countDigitsBefore(value: string, position: number): number {
    return this.getOnlyDigits(value.slice(0, position)).length;
  }

  private mapCursorPosition(digitsBeforeCursor: number, totalDigits: number): number {
    const digits = Math.min(digitsBeforeCursor, totalDigits);

    if (digits === 0) {
      return 0;
    }

    if (digits <= 3) {
      return digits + 1 + (digits === 3 ? 1 : 0);
    }

    if (digits <= 6) {
      return digits + 3;
    }

    return digits + 4;
  }

  private applyValue(digits: string, rawCursorPosition: number, updateControl = true, input?: HTMLInputElement, initialValue: boolean = false): void {
    const truncatedDigits = digits.slice(0, 10);
    const formattedValue = this.formatValue(truncatedDigits);

    this.updateInputValue(formattedValue);

    if (updateControl) {
      this.updateControlValue(truncatedDigits);
    }

    this.setCursorPosition(this.mapCursorPosition(Math.max(rawCursorPosition, 0), truncatedDigits.length));

    if (!initialValue && input?.value !== this.elementRef.nativeElement.value)
      this.elementRef.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private setCursorPosition(position: number): void {
    const input = this.elementRef.nativeElement;
    window.requestAnimationFrame(() => {
      const normalizedPosition = Math.min(position, input.value.length);
      input.setSelectionRange(normalizedPosition, normalizedPosition);
    });
  }

  private updateInputValue(value: string): void {
    const input = this.elementRef.nativeElement;
    this.renderer.setProperty(input, 'value', value);
  }

  private updateControlValue(digits: string): void {
    if (!this.ngControl?.control) {
      return;
    }

    this.isApplyingValue = true;
    this.ngControl.control.setValue(digits, {
      emitEvent: true,
      emitModelToViewChange: false,
    });
    this.isApplyingValue = false;
  }

  private formatInitialValue(): void {
    const initialValue = this.ngControl?.control?.value ?? this.elementRef.nativeElement.value;
    const digits = this.getOnlyDigits((initialValue ?? '').toString()).slice(0, 10);
    if (digits) {
      this.applyValue(digits, digits.length, false);
    }
  }
}
